import { CreditPortfolio } from './credit-portfolio.model'
import { CreditCalculationStructure, CreditStructure } from './credit-structure.model'

export type CreditSubmissionStatusCombined =
  CreditSubmissionStatus | CreditSubmissionReinsurerStatus

export interface CreditSubmissionStructure {
  id?: number
  credit_structure_id: number // id of CreditStructure that submission was generated from
  credit_submission_structure_id: string // credit_calculation_structure_id of snapshot structure generated for submission
  program_id: number
  label: string
  description: string
  transaction_id: string
  status: CreditSubmissionStatus
  selected_gls: string // comma separated _id from CreditGrossLossScenario values
  portfolio_id: string
  doc_link: string
  approved_by?: string
  approved_at?: string
  sub_status?: CreditSubmissionSubStatus
  comments?: string
  reinsurer_ids?: number[]
  removed_reinsurers?: number[]
  reinsurer_status?: CreditSubmissionReinsurerStatus | null
  // below used to manage UI state / not saved to db
  checked?: boolean
  imageSrc?: string
  year?: number
  audit_insert_dt?: string
  client_id?: number
  carrier_id?: number
  carrier_name?: string
  carrier_year_id?: number
  effective_date?: string
  statusClass?: CreditSubmissionStatusClass
  reinsurerStatusClass?: CreditSubmissionReinsurerStatusClass
  userIds?: number[]
  removedUserIds?: number[]
  addedUserIds?: number[]
}

export enum CreditSubmissionStatus {
  Draft = 'Draft',
  Final = 'Final', // Finalized by Lockton
  Approved = 'Approved', // Approved by Client
  Released = 'Released', // Sent to Market (Reinsurer)
  Cancelled = 'Cancelled',
  Deleted = 'Deleted',
}

export const CREDIT_SUBMISSION_STATUS_LIST: CreditSubmissionStatus[] = [
  CreditSubmissionStatus.Draft,
  CreditSubmissionStatus.Final,
  CreditSubmissionStatus.Approved,
  CreditSubmissionStatus.Released,
  CreditSubmissionStatus.Cancelled,
]

export const CREDIT_ADMIN_SUBMISSION_STATUS_LIST: CreditSubmissionStatus[] = [
  ...CREDIT_SUBMISSION_STATUS_LIST,
  CreditSubmissionStatus.Deleted,
]

export enum CreditSubmissionStatusClass {
  Draft = 'app-palette-gray',
  Final = 'app-palette-gold',
  Approved = 'app-palette-green',
  Released = 'app-palette-blue',
  Cancelled = 'app-palette-red',
  Deleted = 'app-palette-red',
}

export enum CreditSubmissionSubStatus {
  ApprovalPending = 'Approval Pending',
  ClientRejected = 'Client Rejected',
}

export enum CreditSubmissionSubStatusClass {
  ApprovalPending = 'app-palette-gold',
  ClientRejected = 'app-palette-red',
}

export interface CreditSubmissionReinsurer {
  id: number
  credit_submission_id: number // id of CreditSubmissionStructure reinsurer associated with
  reinsurer_id: number
  status: CreditSubmissionReinsurerStatus
  viewed_by: string
  viewed_at: string
}

export enum CreditSubmissionReinsurerStatus {
  Pending = 'Pending',
  Viewed = 'Viewed',
  Quoted = 'Quoted',
  Cancelled = 'Cancelled',
  FOT = 'FOT',
  Authorized = 'Authorized',
  Signed = 'Signed',
  Declined = 'Declined',
}

export enum CreditSubmissionReinsurerStatusClass {
  Pending = 'app-palette-gray',
  Viewed = 'app-palette-blue',
  Quoted = 'app-palette-gold',
  FOT = 'app-palette-purple',
  Authorized = 'app-palette-green',
  Signed = 'app-palette-teal',
  Cancelled = 'app-palette-red',
  Declined = 'app-palette-gray',
}

export interface CreditSubmissionStructureWithProgramData {
  year: number
  portfolio_id: string
  carrier_id: number
  credit_submission_structure: CreditSubmissionStructure
  effective_date: string
  reinsurer_status?: CreditSubmissionReinsurerStatus | null
}

export interface CreditSubmissionYears {
  year: string
  ids: string[]
}

export interface ClientSearch {
  clients: string[]
  yearIds: string[]
  status?: CreditSubmissionStatusCombined[] // dependent on internal vs external user
}

export interface SubmissionResults {
  initialStructures: CreditSubmissionStructure[][]
  imageIds: Object
}

export interface CreateSubmissionDraftProps {
  portfolio: CreditPortfolio
  creditStructure: CreditStructure
  calculationStructure: CreditCalculationStructure
  transactionId: string
  description: string
}

export interface CreateSubmissionSuccessProps {
  submissionCount: number
  submissionUserTrancheCount: number
}

export interface SubmissionReinsurer {
  reinsurerId: number
  reinsurerName: string
}

export interface CreditSubmissionUserTranche {
  submission_id: number
  user_id: number
  tranche_id: string
  rol: number // N/A for QS
  reinsurer_participation_percent: number
  collateral_percent: number
  ceding_commission: number // N/A for XOL & ILN
  profit_commission: number // N/A for XOL & ILN
  margin: number // N/A for XOL & ILN
  status: CreditSubmissionUserTrancheStatus
  rorac_scenario: string
}

export interface CreditSubmissionUserTrancheInputs {
  transactionId: string
  trancheId: string // transactionId for QS
  defaultRol: number
  defaulReinsurerParticipationPercent: number
  defaultCollateralPercent: number
  defaultCedingComission: number
  defaultProfitComission: number
  defaultMargin: number
  defaultRoracScenario: string
}

export enum CreditSubmissionUserTrancheStatus {
  Granted = 'Granted',
  Cancelled = 'Cancelled',
}

export interface CreditSubmissionAuthorizeQuoteDialogData {
  reinsurer_participation_percent: number
  reinsurer_participation_dollar?: number
  rol: number
  margin: number
  ceding_commission: number
  profit_commission: number
  tranche_id: string
  comment: string
  legalEntity: string
}

export interface CreditSubmissionAuthorizationRequest {
  submission_id: number
  tranche_id: string
  rol: number
  margin: number
  ceding_commission: number
  profit_commission: number
  reinsurer_participation_percent: number
  reinsurer_participation_dollar?: number
  comments: string
  legalEntity: string
}

export type CreditSubmissionTreeNode = {
  nodeId: number
  nodeType: CreditSubmissionTreeNodeType
  nodeTypeId: number // reinsurerId or userId
  isChecked: boolean | 'mixed'
}

export type CreditSubmissionTreeNodeType = 'reinsurer' | 'user'
